import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import axios from "axios";

import * as XLSX from "xlsx";

import {
  FaArrowLeft,
  FaChevronRight,
  FaFileExcel,
  FaMoneyBillWave,
  FaReceipt,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";

import "./ProfitLossReport.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const getHeaders = () => {

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("access_token") ||
    "";


  const schoolId =
    localStorage.getItem("school_id") ||
    localStorage.getItem("schoolId") ||
    localStorage.getItem(
      "selected_school_id"
    ) ||
    "";


  return {

    Authorization:
      token
        ? `Bearer ${token}`
        : "",

    "X-School-Id":
      schoolId,

    Accept:
      "application/json",
  };
};


const money = (value) =>
  new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );


const ProfitLossMonthPage = () => {

  const { month } =
    useParams();


  const navigate =
    useNavigate();


  const location =
    useLocation();


  const [data, setData] =
    useState({

      month:
        month,

      month_name:
        location.state
          ?.monthName ||
        month,

      summary: {},

      collection_groups:
        [],

      salary_details:
        [],

      expense_details:
        [],

      expense_breakdown:
        [],
    });


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  /* ========================================================
     LOAD
  ======================================================== */

  const loadDetails = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/profit-loss/month-details`,

          {

            params: {
              month,
            },

            headers:
              getHeaders(),
          }
        );


      if (
        response?.data?.status
      ) {

        const result =
          response.data.data ||
          {};


        setData({

          month:
            result.month ||
            month,

          month_name:
            result.month_name ||
            location.state
              ?.monthName ||
            month,

          summary:
            result.summary ||
            {},

          collection_groups:
            Array.isArray(
              result.collection_groups
            )
              ? result.collection_groups
              : [],

          salary_details:
            Array.isArray(
              result.salary_details
            )
              ? result.salary_details
              : [],

          expense_details:
            Array.isArray(
              result.expense_details
            )
              ? result.expense_details
              : [],

          expense_breakdown:
            Array.isArray(
              result.expense_breakdown
            )
              ? result.expense_breakdown
              : [],
        });

      } else {

        setError(
          response?.data?.message ||
          "Unable to load P&L details."
        );
      }

    } catch (err) {

      console.error(
        err
      );


      setError(
        err?.response?.data
          ?.message ||
        "Unable to load P&L details."
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    loadDetails();

  }, [month]);


  const summary =
    data.summary ||
    {};


  const feeGroups =
    Array.isArray(
      data.collection_groups
    )
      ? data.collection_groups
      : [];


  const salaries =
    Array.isArray(
      data.salary_details
    )
      ? data.salary_details
      : [];


  const expenses =
    Array.isArray(
      data.expense_details
    )
      ? data.expense_details
      : [];


  const manualSalaryExpenses =
    useMemo(
      () =>
        expenses.filter(
          (item) =>
            item.expense_group ===
            "salary"
        ),
      [expenses]
    );


  const otherExpenses =
    useMemo(
      () =>
        expenses.filter(
          (item) =>
            item.expense_group !==
            "salary"
        ),
      [expenses]
    );


  const net =
    Number(
      summary.net_profit_loss ||
      0
    );


  const isProfit =
    net >= 0;


  /* ========================================================
     OPEN FEE
  ======================================================== */

  const openFee = (
    group
  ) => {

    const feeType =
      group.fee_type ||
      "Other";


    navigate(

      `/admin/reports/profit-loss/${month}/fee/${encodeURIComponent(
        feeType
      )}`,

      {

        state: {

          monthName:
            data.month_name,

          feeType:
            feeType,

          totalAmount:
            group.total_amount,

          totalTransactions:
            group.total_transactions,
        },
      }
    );
  };


  /* ========================================================
     EXCEL - MONTH SUMMARY
  ======================================================== */

  const exportExcel = () => {

    const workbook =
      XLSX.utils.book_new();


    const summaryRows = [

      {
        Particular:
          "Total Collection",
        Amount:
          Number(
            summary.collection ||
            0
          ),
      },

      {
        Particular:
          "Total Salary Expense",
        Amount:
          Number(
            summary.salary_expense ||
            0
          ),
      },

      {
        Particular:
          "Other Expense",
        Amount:
          Number(
            summary.other_expense ||
            0
          ),
      },

      {
        Particular:
          "Total Expense",
        Amount:
          Number(
            summary.total_expense ||
            0
          ),
      },

      {
        Particular:
          "Net Profit / Loss",
        Amount:
          Number(
            summary.net_profit_loss ||
            0
          ),
      },
    ];


    const summarySheet =
      XLSX.utils.json_to_sheet(
        summaryRows
      );


    XLSX.utils.book_append_sheet(
      workbook,
      summarySheet,
      "P&L Summary"
    );


    const feeRows =
      feeGroups.map(
        (
          group,
          index
        ) => ({

          "#":
            index + 1,

          "Fee Type":
            group.fee_type ||
            "Other",

          "Transactions":
            Number(
              group.total_transactions ||
              0
            ),

          "Total Collection":
            Number(
              group.total_amount ||
              0
            ),
        })
      );


    feeRows.push({

      "#":
        "",

      "Fee Type":
        "GRAND TOTAL",

      "Transactions":
        "",

      "Total Collection":
        Number(
          summary.collection ||
          0
        ),
    });


    const feeSheet =
      XLSX.utils.json_to_sheet(
        feeRows
      );


    XLSX.utils.book_append_sheet(
      workbook,
      feeSheet,
      "Fee Summary"
    );


    const salarySheet =
      XLSX.utils.json_to_sheet(

        salaries.map(
          (
            row,
            index
          ) => ({

            "#":
              index + 1,

            Employee:
              row.employee_name ||
              "",

            Designation:
              row.designation ||
              "",

            Amount:
              Number(
                row.amount ||
                0
              ),
          })
        )
      );


    XLSX.utils.book_append_sheet(
      workbook,
      salarySheet,
      "Salary"
    );


    const expenseSheet =
      XLSX.utils.json_to_sheet(

        expenses.map(
          (
            row,
            index
          ) => ({

            "#":
              index + 1,

            Date:
              row.expense_date ||
              "",

            Category:
              row.category_name ||
              "",

            Title:
              row.title ||
              "",

            Amount:
              Number(
                row.amount ||
                0
              ),
          })
        )
      );


    XLSX.utils.book_append_sheet(
      workbook,
      expenseSheet,
      "Expenses"
    );


    XLSX.writeFile(
      workbook,
      `Profit_Loss_${month}.xlsx`
    );
  };


  if (loading) {

    return (

      <div className="pl-page">

        <div className="pl-loading">

          <div className="pl-loader" />

          Loading P&L...

        </div>

      </div>
    );
  }


  return (

    <div className="pl-page">


      {/* TOOLBAR */}

      <div className="pl-detail-toolbar">

        <button
          type="button"
          className="pl-back-btn"
          onClick={() =>
            navigate(
              "/admin/reports/profit-loss"
            )
          }
        >

          <FaArrowLeft />

          P&L Report

        </button>


        <button
          type="button"
          className="pl-excel-btn"
          onClick={
            exportExcel
          }
        >

          <FaFileExcel />

          Excel

        </button>

      </div>


      {error && (

        <div className="pl-error">
          {error}
        </div>

      )}


      {/* HERO */}

      <div className="pl-hero">

        <div className="pl-hero-icon">
          <FaWallet />
        </div>

        <div>

          <span>
            MONTHLY PROFIT & LOSS
          </span>

          <h1>
            {data.month_name}
          </h1>

          <p>
            Collection minus salary
            and approved expenses.
          </p>

        </div>

      </div>


      {/* SUMMARY */}

      <div className="pl-detail-summary">


        <div className="pl-summary-card">

          <div className="pl-summary-icon green">
            <FaMoneyBillWave />
          </div>

          <div>

            <span>
              Collection
            </span>

            <strong className="pl-green">
              {money(
                summary.collection
              )}
            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div className="pl-summary-icon purple">
            <FaUserTie />
          </div>

          <div>

            <span>
              Salary Expense
            </span>

            <strong>
              {money(
                summary.salary_expense
              )}
            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div className="pl-summary-icon orange">
            <FaReceipt />
          </div>

          <div>

            <span>
              Other Expense
            </span>

            <strong className="pl-orange">
              {money(
                summary.other_expense
              )}
            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div className="pl-summary-icon red">
            <FaReceipt />
          </div>

          <div>

            <span>
              Total Expense
            </span>

            <strong className="pl-red">
              {money(
                summary.total_expense
              )}
            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div
            className={`pl-summary-icon ${
              isProfit
                ? "green"
                : "red"
            }`}
          >
            <FaWallet />
          </div>

          <div>

            <span>
              {isProfit
                ? "Net Profit"
                : "Net Loss"}
            </span>

            <strong
              className={
                isProfit
                  ? "pl-green"
                  : "pl-red"
              }
            >
              {money(net)}
            </strong>

          </div>

        </div>

      </div>


      {/* ==============================================
          FEE COLLECTION SUMMARY
      ============================================== */}

      <div className="pl-card pl-section-card">


        <div className="pl-card-head">

          <div>

            <h2>
              Fee Collection
            </h2>

            <p>
              Fee type-wise collection
              summary. Click View for
              individual transactions.
            </p>

          </div>


          <strong className="pl-green">

            {money(
              summary.collection
            )}

          </strong>

        </div>


        <div className="pl-table-wrap">

          <table className="pl-table pl-fee-summary-table">


            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Fee Type
                </th>

                <th className="center">
                  Transactions
                </th>

                <th className="right">
                  Total Collection
                </th>

                <th className="center">
                  Action
                </th>

              </tr>

            </thead>


            <tbody>

              {feeGroups.map(
                (
                  group,
                  index
                ) => (

                  <tr
                    key={
                      `${group.fee_type}-${index}`
                    }
                  >

                    <td>
                      {index + 1}
                    </td>


                    <td>

                      <div className="pl-fee-name">

                        <span className="pl-fee-name-icon">

                          <FaMoneyBillWave />

                        </span>

                        <strong>

                          {group.fee_type ||
                            "Other"}

                        </strong>

                      </div>

                    </td>


                    <td className="center">

                      <span className="pl-count-badge">

                        {group.total_transactions ||
                          0}

                      </span>

                    </td>


                    <td className="right">

                      <strong className="pl-green">

                        {money(
                          group.total_amount
                        )}

                      </strong>

                    </td>


                    <td className="center">

                      <button
                        type="button"
                        className="pl-view-btn"
                        onClick={() =>
                          openFee(
                            group
                          )
                        }
                      >

                        View

                        <FaChevronRight />

                      </button>

                    </td>

                  </tr>

                )
              )}


              {!feeGroups.length && (

                <tr>

                  <td
                    colSpan="5"
                    className="pl-empty"
                  >
                    No fee collection found.
                  </td>

                </tr>

              )}

            </tbody>


            {!!feeGroups.length && (

              <tfoot>

                <tr>

                  <td colSpan="3">
                    GRAND TOTAL COLLECTION
                  </td>

                  <td className="right pl-total-green">

                    {money(
                      summary.collection
                    )}

                  </td>

                  <td />

                </tr>

              </tfoot>

            )}

          </table>

        </div>

      </div>


      {/* PAYROLL SALARY */}

      <div className="pl-card pl-section-card">

        <div className="pl-card-head">

          <div>

            <h2>
              Payroll Salary
            </h2>

            <p>
              Employee salary for{" "}
              {data.month_name}.
            </p>

          </div>

          <strong>
            {money(
              summary.payroll_salary
            )}
          </strong>

        </div>


        <div className="pl-table-wrap">

          <table className="pl-table">

            <thead>

              <tr>

                <th>#</th>
                <th>Employee</th>
                <th>Designation</th>
                <th>Payment Date</th>
                <th>Mode</th>

                <th className="right">
                  Amount
                </th>

              </tr>

            </thead>


            <tbody>

              {salaries.map(
                (
                  row,
                  index
                ) => (

                  <tr
                    key={
                      row.id ||
                      index
                    }
                  >

                    <td>
                      {index + 1}
                    </td>

                    <td>
                      <strong>
                        {row.employee_name ||
                          "-"}
                      </strong>
                    </td>

                    <td>
                      {row.designation ||
                        "-"}
                    </td>

                    <td>
                      {row.payment_date ||
                        "-"}
                    </td>

                    <td>
                      {row.payment_mode ||
                        "-"}
                    </td>

                    <td className="right">
                      {money(
                        row.amount
                      )}
                    </td>

                  </tr>

                )
              )}


              {!salaries.length && (

                <tr>

                  <td
                    colSpan="6"
                    className="pl-empty"
                  >
                    No payroll salary found.
                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

      </div>


      {/* MANUAL SALARY */}

      {!!manualSalaryExpenses.length && (

        <div className="pl-card pl-section-card">

          <div className="pl-card-head">

            <div>

              <h2>
                Manual Salary Expense
              </h2>

              <p>
                Salary entered through
                Expense Management.
              </p>

            </div>

            <strong>
              {money(
                summary.manual_salary
              )}
            </strong>

          </div>


          <div className="pl-table-wrap">

            <table className="pl-table">

              <thead>

                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th className="right">
                    Amount
                  </th>
                </tr>

              </thead>


              <tbody>

                {manualSalaryExpenses.map(
                  (
                    row,
                    index
                  ) => (

                    <tr
                      key={
                        row.id ||
                        index
                      }
                    >

                      <td>
                        {index + 1}
                      </td>

                      <td>
                        {row.expense_date ||
                          "-"}
                      </td>

                      <td>
                        {row.category_name ||
                          "-"}
                      </td>

                      <td>
                        {row.title ||
                          "-"}
                      </td>

                      <td className="right">

                        {money(
                          row.amount
                        )}

                      </td>

                    </tr>

                  )
                )}

              </tbody>

            </table>

          </div>

        </div>

      )}


      {/* OTHER EXPENSE */}

      <div className="pl-card pl-section-card">

        <div className="pl-card-head">

          <div>

            <h2>
              Other Expenses
            </h2>

            <p>
              Approved non-salary
              expenses.
            </p>

          </div>

          <strong className="pl-orange">

            {money(
              summary.other_expense
            )}

          </strong>

        </div>


        <div className="pl-table-wrap">

          <table className="pl-table">

            <thead>

              <tr>

                <th>#</th>
                <th>Date</th>
                <th>Category</th>
                <th>Sub Category</th>
                <th>Title</th>
                <th>Mode</th>

                <th className="right">
                  Amount
                </th>

              </tr>

            </thead>


            <tbody>

              {otherExpenses.map(
                (
                  row,
                  index
                ) => (

                  <tr
                    key={
                      row.id ||
                      index
                    }
                  >

                    <td>
                      {index + 1}
                    </td>

                    <td>
                      {row.expense_date ||
                        "-"}
                    </td>

                    <td>
                      {row.category_name ||
                        "-"}
                    </td>

                    <td>
                      {row.subcategory_name ||
                        "-"}
                    </td>

                    <td>
                      {row.title ||
                        "-"}
                    </td>

                    <td>
                      {row.payment_mode ||
                        "-"}
                    </td>

                    <td className="right pl-orange">

                      {money(
                        row.amount
                      )}

                    </td>

                  </tr>

                )
              )}


              {!otherExpenses.length && (

                <tr>

                  <td
                    colSpan="7"
                    className="pl-empty"
                  >
                    No other expense found.
                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

      </div>


      {/* RESULT */}

      <div
        className={`pl-result-box ${
          isProfit
            ? "profit"
            : "loss"
        }`}
      >

        <div>

          <span>
            MONTH RESULT
          </span>

          <h3>
            {isProfit
              ? "NET PROFIT"
              : "NET LOSS"}
          </h3>

        </div>

        <strong>
          {money(net)}
        </strong>

      </div>

    </div>
  );
};


export default ProfitLossMonthPage;




